import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import { storeLinks, siteConfig } from '@/mocks/data';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首頁', item: 'https://lelyric.sonbook.net/' },
    { '@type': 'ListItem', position: 2, name: '合作購書通路', item: 'https://lelyric.sonbook.net/stores' },
  ],
};

export default function Stores() {
  const section1 = useScrollReveal();
  const section2 = useScrollReveal();

  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title="合作購書通路｜樂律 Lelyric"
        description="查看樂律出版書籍的正式購書通路，包含博客來品牌書目、崧博出版官網及書籍資料庫。"
        canonicalPath="/stores"
        structuredData={breadcrumbSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[100svh] flex items-center bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0">
            <video
              src="https://public.readdy.ai/ai/video_res/01a0ec5d-cae8-7802-83e5-4bf3aac3e850.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80"></div>
          <div className="absolute top-0 left-0 right-0 h-px bg-background-50/6"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-background-50/6"></div>
          <div className="absolute top-6 right-6 md:top-10 md:right-14 select-none pointer-events-none">
            <span className="font-heading text-5xl md:text-7xl font-bold text-background-50/[0.03]">03</span>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 py-16 md:py-20">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-[3rem] md:text-[4rem] font-bold text-background-50 leading-[1.12] tracking-tight mb-4">
                合作購書通路
              </h1>
              <p className="text-base md:text-lg text-background-50/50 max-w-xl mx-auto leading-relaxed font-light tracking-wide">
                以下是樂律目前已確認的合作購書通路，點擊即可前往各通路瀏覽書籍。
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50/20 to-transparent"></div>
        </section>

        {/* Store Links */}
        <section className="w-full min-h-[80svh] flex items-center px-6 md:px-10 py-16 md:py-24 bg-gradient-to-b from-primary-50/70 via-background-50 to-background-50">
          <div className="w-full max-w-4xl mx-auto" ref={section1.ref}>
            <div className={`transition-all duration-700 ease-out ${section1.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center justify-center gap-2.5 mb-6">
                <span className="block w-5 h-px bg-primary-400/50"></span>
                <span className="text-[13px] font-medium text-primary-600 tracking-[0.15em] uppercase whitespace-nowrap">購書通路</span>
                <span className="block w-5 h-px bg-primary-400/50"></span>
              </div>
              <h2 className="font-heading text-[2.25rem] md:text-[2.75rem] font-semibold text-foreground-950 mb-4 leading-[1.15] tracking-tight text-center">
                已建立的正式通路
              </h2>
              <p className="text-base md:text-lg text-foreground-500 max-w-xl mx-auto mb-14 leading-relaxed font-light text-center">
                以下通路皆為樂律已建立的正式合作連結，點擊即可前往。
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {storeLinks.map((store, i) => (
                  <div
                    key={store.name}
                    className={`transition-all duration-600 ease-out delay-[${i * 120}ms] ${
                      section1.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                    }`}
                  >
                    <a
                      href={store.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-7 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-400/30 hover:bg-background-50 transition-all cursor-pointer group"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                        <i className="ri-store-2-line text-primary-600 text-lg"></i>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {store.name}
                      </h3>
                      <p className="text-base text-foreground-500 leading-relaxed">
                        {store.description}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-[15px] text-primary-600 font-medium">
                        前往通路
                        <i className="ri-external-link-line text-[13px] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
                      </div>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reminder */}
        <section className="w-full min-h-[60svh] flex items-center px-6 md:px-10 py-16 md:py-24 bg-gradient-to-b from-primary-100/60 via-background-100 to-background-100" ref={section2.ref}>
          <div className={`w-full max-w-3xl mx-auto text-center transition-all duration-700 ease-out ${section2.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-6">
              <i className="ri-information-line text-primary-600 text-xl"></i>
            </div>
            <h2 className="font-heading text-[2.25rem] md:text-[2.75rem] font-semibold text-foreground-950 mb-5 leading-[1.15] tracking-tight">
              購書提醒
            </h2>
            <p className="text-base md:text-lg text-foreground-600 max-w-xl mx-auto leading-relaxed font-light text-left">
              完整書名、作者、ISBN、出版日期及正式書目資訊，請以崧博出版書籍資料庫及各通路頁面顯示之內容為準。如有購書相關問題，請直接聯繫各通路客服。
            </p>
            <div className="mt-10">
              <a
                href={siteConfig.catalogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2.5 px-7 py-3 rounded-full text-[15px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer tracking-wide"
              >
                前往書籍資料庫
                <i className="ri-external-link-line text-[13px]"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}